import React, { useEffect, useRef, useState } from "react";
import {
  getRealEstateData,
  updateRealEstateLike,
  RefreshAllSales,
} from "../api";
import HeartToggleButton from "../components/Estate/HeartToggleButton";
import SearchBar from "../components/Estate/SearchBar";
import LikedModal from "../components/Estate/LikedModal";
import EstateDetailModal from "../components/Estate/EstateDetailModal";
import FilterPanel from "../components/FilterPanel";

declare global {
  interface Window {
    kakao: any;
  }
}

const KakaoMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const mapInstance = useRef<any>(null);
  const openInfoWindowRef = useRef<any>(null);
  const markerRefs = useRef<any[]>([]);
  const markerMap = useRef<{ [key: string]: any }>({}); // idx별 마커 참조
  const infoWindowMap = useRef<{ [key: string]: any }>({}); // idx별 infowindow 참조
  const [showLikedOnly, setShowLikedOnly] = useState(false);
  const [showLikedModal, setShowLikedModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minYear, setMinYear] = useState("");
  const [maxYear, setMaxYear] = useState("");

  // Function to render markers and infowindows
  const renderMarkers = (map: any, data: any[]) => {
    markerRefs.current.forEach((marker) => marker.setMap(null));
    markerRefs.current = [];
    markerMap.current = {};
    infoWindowMap.current = {};

    const redMarkerImage = new window.kakao.maps.MarkerImage(
      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='40'><ellipse cx='16' cy='20' rx='12' ry='16' fill='red' stroke='black' stroke-width='2'/></svg>",
      new window.kakao.maps.Size(32, 40),
      { offset: new window.kakao.maps.Point(16, 40) }
    );

    data.forEach((item: any) => {
      const lat = parseFloat(item.map_y);
      const lng = parseFloat(item.map_x);
      if (isNaN(lat) || isNaN(lng)) return;

      const markerPosition = new window.kakao.maps.LatLng(lat, lng);
      const marker = new window.kakao.maps.Marker({
        position: markerPosition,
        map: map,
        image: item.is_liked ? redMarkerImage : undefined,
      });
      markerRefs.current.push(marker);
      markerMap.current[item.idx] = marker;

      // 마커 클릭 시 모달로 상세 정보 표시
      window.kakao.maps.event.addListener(marker, "click", () => {
        setSelectedItem(item);
      });
    });
  };

  useEffect(() => {
    const initializeMap = () => {
      window.kakao.maps.load(async () => {
        const container = mapRef.current;
        const options = {
          center: new window.kakao.maps.LatLng(37.5665, 126.978),
          level: 7,
        };

        const map = new window.kakao.maps.Map(container, options);
        mapInstance.current = map;

        try {
          const data = await getRealEstateData();
          setData(data);
          renderMarkers(map, data);

          window.kakao.maps.event.addListener(map, "click", () => {
            if (openInfoWindowRef.current) {
              openInfoWindowRef.current.close();
              openInfoWindowRef.current = null;
            }
          });
        } catch (err) {
          console.error("❌ 데이터 불러오기 실패:", err);
        }
      });
    };

    if (window.kakao && window.kakao.maps) {
      initializeMap();
    } else {
      const script = document.createElement("script");
      script.src =
        "//dapi.kakao.com/v2/maps/sdk.js?autoload=false&appkey=ca6ab619290db199a73b3a9cf99daac9";
      script.async = true;
      script.onload = initializeMap;
      document.head.appendChild(script);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (mapInstance.current && data.length > 0) {
      renderMarkers(
        mapInstance.current,
        showLikedOnly ? data.filter((item) => item.is_liked) : data
      );
    }
    // eslint-disable-next-line
  }, [data, showLikedOnly]);

  // 필터 핸들러
  const handleFilterChange = (field: string, value: string) => {
    if (field === "minPrice") setMinPrice(value);
    if (field === "maxPrice") setMaxPrice(value);
    if (field === "minYear") setMinYear(value);
    if (field === "maxYear") setMaxYear(value);
  };
  const handleFilterReset = () => {
    setMinPrice("");
    setMaxPrice("");
    setMinYear("");
    setMaxYear("");
  };

  // 필터링된 데이터
  const filteredData = data.filter((item) => {
    const price = parseInt(item.deal_amount, 10);
    const year = parseInt(
      item.construction_year || item.build_year || item.year || "",
      10
    );
    let pass = true;
    if (minPrice && price < parseInt(minPrice, 10)) pass = false;
    if (maxPrice && price > parseInt(maxPrice, 10)) pass = false;
    if (minYear && (!year || year < parseInt(minYear, 10))) pass = false;
    if (maxYear && (!year || year > parseInt(maxYear, 10))) pass = false;
    return pass;
  });

  // 좋아요 데이터만 필터링
  const likedData = filteredData.filter((item) => item.is_liked);

  // 검색 핸들러
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!search.trim()) return;
    const results = filteredData.filter(
      (item) =>
        String(item.idx).includes(String(search.trim())) ||
        (item.address && item.address.includes(search.trim()))
    );
    setSearchResults(results);
    setShowModal(true);
  };

  // 매물 선택 시 지도 이동 및 모달 오픈
  const handleSelectItem = (item: any) => {
    setShowModal(false);
    if (!mapInstance.current) return;
    const lat = parseFloat(item.map_y);
    const lng = parseFloat(item.map_x);
    if (isNaN(lat) || isNaN(lng)) return;
    mapInstance.current.setCenter(new window.kakao.maps.LatLng(lat, lng));
    setSelectedItem(item);
  };

  // 좋아요(하트) 버튼 클릭 시 핸들러
  const handleHeartToggle = () => {
    setShowLikedOnly((prev) => {
      const next = !prev;
      setShowLikedModal(next);
      return next;
    });
  };

  // 금액을 '15억 2천만 원' 형태로 변환하는 함수 (억만 있으면 '2억', 억+천 있으면 '2억 3천만 원', 천만 있으면 '3천만 원', 그 외는 '500만원')
  function formatKoreanMoney(amount: any) {
    const num = parseInt(amount, 10);
    if (isNaN(num)) return amount;
    const eok = Math.floor(num / 10000);
    const chun = Math.floor((num % 10000) / 1000);
    if (eok > 0 && chun > 0) return `${eok}억 ${chun}천만 원`;
    if (eok > 0 && chun === 0) return `${eok}억`;
    if (eok === 0 && chun > 0) return `${chun}천만 원`;
    return num.toLocaleString() + "만원";
  }

  // 좋아요 토글(상세 모달에서)
  const handleLike = async (item: any) => {
    try {
      await updateRealEstateLike(item.idx, !item.is_liked);
      const newData = await getRealEstateData();
      setData(newData);
      setSelectedItem(null);
    } catch (err) {
      alert("좋아요 처리에 실패했습니다.");
    }
  };

  // 최신화 버튼 클릭 핸들러
  const handleRefresh = async () => {
    try {
      await RefreshAllSales();
      const newData = await getRealEstateData();
      setData(newData);
    } catch (err) {
      alert("최신화에 실패했습니다.");
    }
  };

  return (
    <div className="w-screen h-screen relative">
      <FilterPanel
        minPrice={minPrice}
        maxPrice={maxPrice}
        minYear={minYear}
        maxYear={maxYear}
        onChange={handleFilterChange}
        onReset={handleFilterReset}
      />
      <button
        onClick={handleRefresh}
        className="fixed top-8 right-8 z-50 bg-white border border-gray-200 rounded-lg shadow px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition"
        type="button"
      >
        최신화하기
      </button>
      <HeartToggleButton isActive={showLikedOnly} onClick={handleHeartToggle} />
      <SearchBar
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onSubmit={handleSearch}
      />
      {/* 검색 결과 모달 */}
      {showModal && (
        <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-40 bg-white rounded-xl shadow-lg min-w-[300px] max-h-[400px] overflow-y-auto p-4">
          <div className="mb-2 font-bold flex justify-between items-center">
            검색 결과 ({searchResults.length}건)
            <button
              onClick={() => setShowModal(false)}
              className="text-lg text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          {searchResults.length === 0 ? (
            <div>검색 결과가 없습니다.</div>
          ) : (
            <ul className="list-none p-0 m-0">
              {searchResults.map((item) => (
                <li
                  key={item.idx}
                  className="py-2 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSelectItem(item)}
                >
                  <span className="font-bold">{item.idx}</span> | {item.address}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {/* 좋아요 모달 */}
      {showLikedModal && (
        <LikedModal
          likedData={likedData}
          onSelect={handleSelectItem}
          formatKoreanMoney={formatKoreanMoney}
          onClose={() => setShowLikedModal(false)}
        />
      )}
      {/* 상세 모달 */}
      {selectedItem && (
        <EstateDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onLike={() => handleLike(selectedItem)}
          formatKoreanMoney={formatKoreanMoney}
        />
      )}
      {/* 지도 */}
      <div
        ref={mapRef}
        className="w-screen h-screen absolute left-0 top-0 z-0"
      />
    </div>
  );
};

export default KakaoMap;
